import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/services/api-client';

export interface TestUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  personaId: string | null;
  personaName: string | null;
  personaCode: string | null;
}

export const TEST_USERS_QUERY_KEY = ['admin', 'test-users'] as const;

export async function fetchTestUsers(): Promise<TestUser[]> {
  const response = await apiClient.get<TestUser[]>('/api/v1/admin/test-users');
  if (!response.success) {
    throw new Error(response.error?.message ?? 'Failed to load test users');
  }
  return (response.data as unknown as TestUser[]) ?? [];
}

interface TestUserPickerProps {
  selectedUserId: string | null;
  onSelect: (user: TestUser | null) => void;
}

function labelFor(u: TestUser): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email.split('@')[0];
  return u.personaName ? `${name} — ${u.personaName}` : name;
}

/**
 * Compact inline picker for "Test as user". One line — label + dropdown
 * (+ clear button when a user is selected). Parent owns `selectedUserId`
 * (driven by a URL query param so refresh preserves the choice).
 */
export function TestUserPicker({ selectedUserId, onSelect }: TestUserPickerProps) {
  const {
    data: users,
    isLoading,
    error,
  } = useQuery<TestUser[]>({
    queryKey: TEST_USERS_QUERY_KEY,
    queryFn: fetchTestUsers,
    staleTime: 60_000,
  });

  function handleChange(id: string) {
    onSelect(users?.find((u) => u.id === id) ?? null);
  }

  if (error) {
    return (
      <p className="text-xs text-destructive">
        Unable to load test users: {error instanceof Error ? error.message : 'unknown error'}
      </p>
    );
  }

  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
        Test as
      </span>
      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      ) : (
        <Select
          value={selectedUserId ?? ''}
          onValueChange={handleChange}
          disabled={(users?.length ?? 0) === 0}
        >
          <SelectTrigger aria-label="Choose a test user" className="h-8 w-[18rem] text-sm">
            <SelectValue placeholder="Yourself" />
          </SelectTrigger>
          <SelectContent>
            {users?.map((u) => (
              <SelectItem key={u.id} value={u.id} className="text-sm">
                {labelFor(u)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {selectedUserId && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          aria-label="Clear test user selection"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        >
          <X className="h-3 w-3" />
          Clear
        </button>
      )}
    </div>
  );
}
